import type { Module } from 'vuex';
import { Currency } from '@/types/Currency';
import {RootState} from "@/store";

export interface CurrenciesState {
  currencies: Currency[];
}

const currenciesModule: Module<CurrenciesState, RootState> = {
  namespaced: true,

  state: (): CurrenciesState => ({
    currencies: [],
  }),

  getters: {
    getById: (state) => (idCurrency: number): Currency | undefined => {
      return state.currencies.find(currency => currency.idCurrency === idCurrency);
    },
    getByCode: (state) => (code: string): Currency | undefined => {
      return state.currencies.find(currency => currency.code === code);
    },
  },

  mutations: {
    setCurrencies(
      state,
      payload: { currencies: Record<number, any> }
    ) {
      state.currencies = Object
        .values(payload.currencies)
        .map(raw => Currency.fromRaw({
          ...raw,
          blockchains: Array.isArray(raw.blockchains) ? raw.blockchains : [],
        }));
    },

    setCurrencyCreate(
      state,
      payload: {
        idCurrency: number;
        code: string;
        name: string | null;
        description: string | null;
        locked: boolean;
        blockchains?: any[];
      }
    ) {
      const raw = {
        idCurrency: payload.idCurrency,
        code: payload.code,
        name: payload.name,
        description: payload.description,
        locked: payload.locked,
        blockchains: Array.isArray(payload.blockchains) ? payload.blockchains : [],
      };
      state.currencies.push(Currency.fromRaw(raw));
    },

    setCurrencyUpdate(
      state,
      payload: {
        idCurrency: number;
        code?: string;
        name?: string | null;
        description?: string | null;
      }
    ) {
      const currency = state.currencies.find(currency => currency.idCurrency === payload.idCurrency);
      if (!currency) return;
      if (payload.code !== undefined) currency.code = payload.code;
      if (payload.name !== undefined) currency.name = payload.name;
      if (payload.description !== undefined) currency.description = payload.description;
    },

    setCurrencyDelete(
      state,
      payload: { idCurrency: number }
    ) {
      state.currencies = state.currencies.filter(
        currency => currency.idCurrency !== payload.idCurrency
      );
    },

    setCurrencyLock(
      state,
      payload: { idCurrency: number }
    ) {
      const currency = state.currencies.find(currency => currency.idCurrency === payload.idCurrency);
      if (currency) currency.locked = true;
    },

    setCurrencyUnlock(
      state,
      payload: { idCurrency: number }
    ) {
      const currency = state.currencies.find(currency => currency.idCurrency === payload.idCurrency);
      if (currency) currency.locked = false;
    },
  },

  actions: {
    wsSetCurrencies({ commit }, payload) {
      commit('setCurrencies', payload);
    },
    wsCurrencyCreate({ commit }, payload) {
      commit('setCurrencyCreate', payload);
    },
    wsCurrencyUpdate({ commit }, payload) {
      commit('setCurrencyUpdate', payload);
    },
    wsCurrencyDelete({ commit }, payload) {
      commit('setCurrencyDelete', payload);
    },
    wsCurrencyLock({ commit }, payload) {
      commit('setCurrencyLock', payload);
    },
    wsCurrencyUnlock({ commit }, payload) {
      commit('setCurrencyUnlock', payload);
    },
  },
};

export default currenciesModule;
